import { forwardRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInvoiceTheme } from '../hooks/use-invoice-theme';

interface ExchangeCar {
  id: number;
  personOneCarBrand: string;
  personOneCarModel: string;
  personOneCarYear: number;
  personOneCarPlateNo: string;
  personOneCarChassisNo?: string | null;
  personOneCarColor: string;
  personOneName: string;
  personOneIdNo: string;
  personOneAddress: string | null;
  personOneContact: string;
  personTwoCarBrand: string;
  personTwoCarModel: string;
  personTwoCarYear: number;
  personTwoCarPlateNo: string;
  personTwoCarChassisNo?: string | null;
  personTwoCarColor: string;
  personTwoName: string;
  personTwoIdNo: string;
  personTwoAddress: string | null;
  personTwoContact: string;
  amountOwed: number;
  paidAmount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ExchangeCarInvoiceProps {
  exchangeCar: ExchangeCar;
  isReprint?: boolean;
}

const ExchangeCarInvoice = forwardRef<HTMLDivElement, ExchangeCarInvoiceProps>(({ exchangeCar }, ref) => {
  const { t, language } = useLanguage();
  const { colors } = useInvoiceTheme();

  // Custom translation function for invoice - use Kurdish when language is English
  const tInvoice = (key: string) => {
    if (language === 'english') {
      // For English language, use Kurdish translations (same as CarInvoice)
      const kurdishTranslations = {
        'invoice.storeName': 'پێشانگای دارا',
        'invoice.date': 'بەروار',
        'invoice.time': 'کات',
        'exchangeInvoice.exchangeId': 'ژمارەی گۆڕین',
        'exchangeInvoice.personOne': 'لایەنی یەکەم',
        'exchangeInvoice.personTwo': 'لایەنی دووەم',
        'exchangeInvoice.carDetails': 'وردەکارییەکانی ئۆتۆمبێل',
        'exchangeInvoice.personalDetails': 'وردەکارییەکانی کەسی',
        'exchangeInvoice.brand': 'مارکە',
        'exchangeInvoice.model': 'مۆدێل',
        'exchangeInvoice.year': 'ساڵ',
        'exchangeInvoice.color': 'ڕەنگ',
        'exchangeInvoice.plateNumber': 'ژمارەی تابلۆ',
        'exchangeInvoice.chassisNumber': 'ژمارەی شاصی',
        'exchangeInvoice.name': 'ناو',
        'exchangeInvoice.idNo': 'ژمارەی ناسنامە',
        'exchangeInvoice.address': 'ناونیشان',
        'exchangeInvoice.contact': 'تەلەفۆن',
        'exchangeInvoice.amountOwed': 'بڕی قەرز',
        'exchangeInvoice.paidAmount': 'بڕی پارەدراو',
        'exchangeInvoice.financialDetails': 'وردەکارییەکانی پارە',
        'exchangeInvoice.remainingBalance': 'بڕی ماوە',
        'exchangeInvoice.exchangeAgreement': 'گرێبەستی گۆڕینی ئۆتۆمبێل',
        'exchangeInvoice.agreementStatement': 'تێبینی: ئەم گرێبەستە واژووکرا دوای ئەوەی تەواوی خاڵەکانی لەلایەن هەردوو لایەن خوێندرایەوە هەروەها ئۆتۆمبێلەکان پێش ئەم گرێبەستە لەلایەن هەردوو لایەن پشکنینی بۆ کرا.',
        'exchangeInvoice.personOneSignature': 'واژووی لایەنی یەکەم',
        'exchangeInvoice.personTwoSignature': 'واژووی لایەنی دووەم',
        'exchangeInvoice.notes': 'تێبینی',
      };
      return kurdishTranslations[key as keyof typeof kurdishTranslations] || t(key);
    }
    return t(key);
  };

  // Get the appropriate font class based on language
  const getFontClass = () => {
    switch (language) {
      case 'kurdish':
      case 'english': // English should display as Kurdish
        return 'invoice-font-kurdish';
      case 'arabic':
        return 'invoice-font-arabic';
      default:
        return 'invoice-font-kurdish';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="hidden">
      <div
        ref={ref}
        className={`bg-white text-black p-0 m-0 ${getFontClass()}`}
        style={{
          width: '210mm',
          minHeight: '297mm',
          maxWidth: '210mm',
          margin: '0 auto',
          fontSize: '10px',
          lineHeight: '1.3',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header Section */}
        <div className="w-full">
          <div className="flex justify-between items-center relative px-3 py-2">
            <div className="w-40 h-24 flex items-center justify-center">
              {/* Empty space for balance */}
            </div>
            <div className="flex-1 flex justify-center items-center relative">
              <h1 className="text-4xl font-bold relative z-10 text-white whitespace-nowrap">
                {tInvoice('invoice.storeName')}
              </h1>
            </div>
            <div className="w-40 h-24 flex items-center justify-center relative">
              <img
                src="/DaraCars.png"
                alt="DaraCars Store"
                className="w-full h-full object-contain relative z-10"
              />
            </div>
            {/* Single background covering both store name and logo area */}
            <div className="absolute inset-0 opacity-80" style={{ backgroundColor: colors.headerBg }}></div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-3 py-2 flex-grow">
          {/* Exchange ID, Date and Time */}
          <div className="mb-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium">{tInvoice('invoice.date')}: <span className="invoice-number">{formatDate(exchangeCar.createdAt)}</span></p>
                <p className="text-sm font-medium">{tInvoice('invoice.time')}: <span className="invoice-number">{formatTime(exchangeCar.createdAt)}</span></p>
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">{tInvoice('exchangeInvoice.exchangeId')}:</span>
                  <span className="text-sm font-bold px-2 py-0.5 rounded invoice-number" style={{ color: colors.accentColor, backgroundColor: colors.accentBg }}>
                    NO: {exchangeCar.id}
                  </span>
                </div>
              </div>
              <div className="text-sm font-medium text-right space-y-0.5">
                <div className="invoice-number phone-number">0770 445 0169</div>
                <div className="invoice-number phone-number">0750 205 0003</div>
                <div className="phone-number" dir="rtl">هەولێر، ٤٠م ئازادی</div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="mb-2 text-center">
            <h2 className="text-xl font-bold" style={{ color: colors.titleColor }}>
              {tInvoice('exchangeInvoice.exchangeAgreement')}
            </h2>
          </div>

          {/* Person One and Person Two Details Table */}
          <div className="mb-2">
            <div className="border-2 border-gray-400">
              <div className="grid grid-cols-2">
                {/* Person One Details Column */}
                <div className="border-r-2 border-gray-600">
                  <div className="p-1.5 border-b-2 border-gray-400" style={{ backgroundColor: colors.headerBg }}>
                    <h3 className="text-lg font-semibold text-center text-white">{tInvoice('exchangeInvoice.personOne')}</h3>
                  </div>

                  {/* Person One Car Details Section */}
                  <div className="border-b-2 border-gray-400">
                    <div className="p-1.5 bg-gray-100 border-b border-gray-300">
                      <h4 className="text-base font-semibold text-center">{tInvoice('exchangeInvoice.carDetails')}</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.brand')}/{tInvoice('exchangeInvoice.model')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span dir="ltr">{exchangeCar.personOneCarBrand} {exchangeCar.personOneCarModel}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.color')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                          <span>{exchangeCar.personOneCarColor}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.year')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span>{exchangeCar.personOneCarYear}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content">
                        <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.plateNumber')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                          <span dir="ltr">{exchangeCar.personOneCarPlateNo}</span>
                        </div>
                      </div>
                      {exchangeCar.personOneCarChassisNo && (
                        <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                          <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                            <span className="font-medium">{tInvoice('exchangeInvoice.chassisNumber')}:</span>
                          </div>
                          <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                            <span dir="ltr">{exchangeCar.personOneCarChassisNo}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Person One Personal Details Section */}
                  <div>
                    <div className="p-1.5 bg-gray-100 border-b border-gray-300">
                      <h4 className="text-base font-semibold text-center">{tInvoice('exchangeInvoice.personalDetails')}</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.name')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span>{exchangeCar.personOneName}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.idNo')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                          <span>{exchangeCar.personOneIdNo}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.contact')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span>{exchangeCar.personOneContact}</span>
                        </div>
                      </div>
                      {exchangeCar.personOneAddress ? (
                        <div className="grid grid-cols-2 invoice-table-content">
                          <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                            <span className="font-medium">{tInvoice('exchangeInvoice.address')}:</span>
                          </div>
                          <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                            <span>{exchangeCar.personOneAddress}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 invoice-table-content">
                          <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                            <span className="font-medium">{tInvoice('exchangeInvoice.address')}:</span>
                          </div>
                          <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                            <span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Person Two Details Column */}
                <div>
                  <div className="p-1.5 border-b-2 border-gray-400" style={{ backgroundColor: colors.headerBg }}>
                    <h3 className="text-lg font-semibold text-center text-white">{tInvoice('exchangeInvoice.personTwo')}</h3>
                  </div>

                  {/* Person Two Car Details Section */}
                  <div className="border-b-2 border-gray-400">
                    <div className="p-1.5 bg-gray-100 border-b border-gray-300">
                      <h4 className="text-base font-semibold text-center">{tInvoice('exchangeInvoice.carDetails')}</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.brand')}/{tInvoice('exchangeInvoice.model')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span dir="ltr">{exchangeCar.personTwoCarBrand} {exchangeCar.personTwoCarModel}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.color')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                          <span>{exchangeCar.personTwoCarColor}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.year')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span>{exchangeCar.personTwoCarYear}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content">
                        <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.plateNumber')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                          <span dir="ltr">{exchangeCar.personTwoCarPlateNo}</span>
                        </div>
                      </div>
                      {exchangeCar.personTwoCarChassisNo && (
                        <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                          <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                            <span className="font-medium">{tInvoice('exchangeInvoice.chassisNumber')}:</span>
                          </div>
                          <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                            <span dir="ltr">{exchangeCar.personTwoCarChassisNo}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Person Two Personal Details Section */}
                  <div>
                    <div className="p-1.5 bg-gray-100 border-b border-gray-300">
                      <h4 className="text-base font-semibold text-center">{tInvoice('exchangeInvoice.personalDetails')}</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.name')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span>{exchangeCar.personTwoName}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.idNo')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                          <span>{exchangeCar.personTwoIdNo}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                        <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                          <span className="font-medium">{tInvoice('exchangeInvoice.contact')}:</span>
                        </div>
                        <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                          <span>{exchangeCar.personTwoContact}</span>
                        </div>
                      </div>
                      {exchangeCar.personTwoAddress ? (
                        <div className="grid grid-cols-2 invoice-table-content">
                          <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                            <span className="font-medium">{tInvoice('exchangeInvoice.address')}:</span>
                          </div>
                          <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                            <span>{exchangeCar.personTwoAddress}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 invoice-table-content">
                          <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                            <span className="font-medium">{tInvoice('exchangeInvoice.address')}:</span>
                          </div>
                          <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                            <span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Amount Owed and Paid Amount */}
          {(exchangeCar.amountOwed > 0 || exchangeCar.paidAmount > 0) && (
            <div className="mb-2">
              <div className="border-2 border-gray-400">
                <div className="p-1.5 border-b-2 border-gray-400" style={{ backgroundColor: colors.headerBg }}>
                  <h3 className="text-lg font-semibold text-center text-white">{tInvoice('exchangeInvoice.financialDetails')}</h3>
                </div>
                <div className="space-y-0">
                  {exchangeCar.amountOwed > 0 && (
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('exchangeInvoice.amountOwed')}:</span>
                      </div>
                      <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                        <span className="text-base font-bold text-red-600 invoice-number">{formatPrice(exchangeCar.amountOwed)}</span>
                      </div>
                    </div>
                  )}
                  {exchangeCar.paidAmount > 0 && (
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-1 px-1.5 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('exchangeInvoice.paidAmount')}:</span>
                      </div>
                      <div className="py-1 px-1.5 bg-white text-right border border-gray-300">
                        <span className="text-base font-bold text-green-600 invoice-number">{formatPrice(exchangeCar.paidAmount)}</span>
                      </div>
                    </div>
                  )}
                  {exchangeCar.amountOwed > 0 && (
                    <div className="grid grid-cols-2 invoice-table-content">
                      <div className="py-1 px-1.5 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('exchangeInvoice.remainingBalance')}:</span>
                      </div>
                      <div className="py-1 px-1.5 bg-gray-50 text-right border border-gray-300">
                        <span className="text-base font-bold text-blue-600 invoice-number">{formatPrice(Math.max(0, exchangeCar.amountOwed - (exchangeCar.paidAmount || 0)))}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Agreement Statement */}
          <div className="mb-1">
            <p className="text-sm" style={{ color: colors.accentColor }}>
              {tInvoice('exchangeInvoice.agreementStatement')}
            </p>
          </div>

          {/* Notes */}
          {exchangeCar.notes && (
            <div className="mb-1">
              <p className="text-sm" style={{ color: colors.noteColor }}>
                <span className="font-medium">{tInvoice('exchangeInvoice.notes')}:</span> {exchangeCar.notes}
              </p>
            </div>
          )}

          {/* Signatures */}
          <div className="mb-2 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-center">{tInvoice('exchangeInvoice.personOneSignature')}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-center">{tInvoice('exchangeInvoice.personTwoSignature')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ExchangeCarInvoice.displayName = 'ExchangeCarInvoice';

export default ExchangeCarInvoice;

